/**
 * 캠페인·사이드바 기준의 포스트 **클라이언트 사이드 필터링**.
 *
 * ⚠️ 포스트 배열은 `unknown[]` 계약이다 — 화면 계층은 포스트 모양을 모른다.
 * 그래서 목록 함수들은 제네릭(`<T>`)으로 받은 원소 타입을 그대로 돌려주고,
 * 판정할 때만 `FilterablePost`(이 파일이 읽는 필드만 옵셔널로 적은 모양)로
 * 좁혀 읽는다. 원소를 바꾸지 않는다.
 */

import { log } from 'src/utils/logger';
import { isPostPersonaSelected } from 'src/utils/monitoring-filters';
import {
  classifyXPostKindChipId,
  classifyInstagramPostKindChipId,
} from 'src/utils/brand/brand-filter-utils';

// 에러 로그 라벨 — 파일 이동(B7)에 맞춰 갱신. 검색어로 로그를 찾을 때의 이름이다
const SERVICE_NAME = 'utils/moment-filter';

const X_KIND_CHIP_IDS = ['x', 'x-quote', 'x-repost'];
const IG_KIND_CHIP_IDS = ['instagram-post', 'instagram-story'];

/** 이 파일이 포스트에서 읽는 것 — 전부 옵셔널이라 어떤 포스트 모양이든 받는다 */
type FilterablePost = {
  source?: unknown;
  platform?: unknown;
  title?: unknown;
  content?: unknown;
  author?: unknown;
  account?: unknown;
  username?: unknown;
  searchPost?: unknown;
  searchLink?: unknown;
  market?: unknown;
  country?: unknown;
  tL?: unknown;
  language?: unknown;
  brands?: unknown;
  sentimentEvidencePositive?: unknown;
  sentimentEvidenceNegative?: unknown;
};

// 포스트 배열 계약(파일 머리말 ⚠️) — 원소를 바꾸지 않고 읽기용으로만 좁힌다
const asFilterable = (post: unknown): FilterablePost => post as FilterablePost;

type PlatformSubChipOptions = {
  isAllBrandChannels?: boolean;
  selectedMainPlatformIds?: string[] | null;
  brandXChannelActive?: boolean;
  brandInstagramChannelActive?: boolean;
};

/**
 * 플랫폼·X 본문/인용/리포스트 칩 필터
 */
const matchesPlatformSubChips = (
  post: FilterablePost,
  platformSet: Set<string>,
  norm: (value: unknown) => string,
  options: PlatformSubChipOptions = {},
): boolean => {
  const isAllBrandChannels = Boolean(options?.isAllBrandChannels);
  const brandXChannelActive = options.brandXChannelActive !== undefined
    ? Boolean(options.brandXChannelActive)
    : true;
  const brandInstagramChannelActive = options.brandInstagramChannelActive !== undefined
    ? Boolean(options.brandInstagramChannelActive)
    : true;
  const postPlatform = norm(post?.source || post?.platform);
  const isX = postPlatform === 'x' || postPlatform === 'twitter';

  const hasXBody = platformSet.has('x');
  const hasXQuote = platformSet.has('x-quote');
  const hasXRepost = platformSet.has('x-repost');
  const hasXKindChips = hasXBody || hasXQuote || hasXRepost;
  const allXKindsSelected = hasXBody && hasXQuote && hasXRepost;

  const hasIgPost = platformSet.has('instagram-post');
  const hasIgStory = platformSet.has('instagram-story');
  const hasIgKindChips = hasIgPost || hasIgStory;
  const allIgKindsSelected = hasIgPost && hasIgStory;

  const mainPlatforms = Array.isArray(options?.selectedMainPlatformIds)
    ? options.selectedMainPlatformIds.map((id) => String(id).toLowerCase()).filter(Boolean)
    : Array.from(platformSet).filter(
      (platformId) => !X_KIND_CHIP_IDS.includes(platformId)
        && !IG_KIND_CHIP_IDS.includes(platformId),
    );
  const hasMainPlatformChips = mainPlatforms.length > 0;

  const skipEmptyPlatformBypass = isAllBrandChannels
    && (!brandXChannelActive || !brandInstagramChannelActive);

  if (
    !hasMainPlatformChips
    && !hasXKindChips
    && !hasIgKindChips
    && !skipEmptyPlatformBypass
  ) {
    return true;
  }
  // 「전체」 채널 + X·Instagram 세부 모두 ON — 플랫폼 제한 없음
  if (
    !hasMainPlatformChips
    && allXKindsSelected
    && allIgKindsSelected
    && isAllBrandChannels
  ) {
    return true;
  }

  if (isX) {
    if (!brandXChannelActive) return false;
    if (hasXKindChips) {
      const kind = classifyXPostKindChipId(post);
      if (kind === 'x-quote') return hasXQuote;
      if (kind === 'x-repost') return hasXRepost;
      return hasXBody;
    }
    if (hasMainPlatformChips) {
      return mainPlatforms.includes('x') || mainPlatforms.includes('twitter');
    }
    if (!isAllBrandChannels && hasIgKindChips) {
      return false;
    }
    return true;
  }

  const isIg = postPlatform === 'instagram';
  if (isIg) {
    if (!brandInstagramChannelActive) return false;
    if (hasIgKindChips) {
      const kind = classifyInstagramPostKindChipId(post);
      if (kind === 'instagram-story') return hasIgStory;
      return hasIgPost;
    }
    if (hasMainPlatformChips) {
      return mainPlatforms.includes('instagram');
    }
    if (!isAllBrandChannels && hasXKindChips) {
      return false;
    }
    return true;
  }

  if (hasMainPlatformChips) {
    return mainPlatforms.includes(postPlatform);
  }

  // 「전체」 채널 — X 서브타입만 조정, 비-X는 포함
  if (isAllBrandChannels) {
    return true;
  }

  // X 칩만 선택 — 비-X 제외
  return false;
};


/**
 * 캠페인 키워드 OR 필터용 텍스트 필드 (ES must의 searchPost와 동일하게 포함)
 */
const collectPostKeywordMatchFields = (post: FilterablePost): string[] => [
  String(post?.account || post?.username || '').toLowerCase(),
  String(post?.title || '').toLowerCase(),
  String(post?.content || '').toLowerCase(),
  String(post?.author || '').toLowerCase(),
  String(post?.searchPost || '').toLowerCase(),
  String(post?.searchLink || '').toLowerCase(),
];

const postMatchesAnyKeyword = (post: FilterablePost, kwLower: string[]): boolean => {
  if (kwLower.length === 0) return true;
  const fields = collectPostKeywordMatchFields(post);
  return kwLower.some((k) => k && fields.some((field) => field.includes(k)));
};

type CampaignFilterCriteria = {
  keywords?: unknown;
  markets?: unknown;
  platforms?: unknown;
  accounts?: unknown;
};

/**
 * 캠페인 기준에 따라 포스트를 필터링합니다.
 * - keywords: 키워드 중 하나라도 포함되면 됨 (OR 조건, searchPost 포함)
 * - markets: 설정한 국가들 중 하나라도 매치 (OR 조건)
 * - platforms: 설정한 플랫폼들 중 하나라도 매치 (OR 조건)
 * - accounts: 설정한 계정들 중 하나라도 매치 (OR 조건)
 */
export const filterPostsByCampaignCriteria = <T,>(
  posts: T[] | null | undefined,
  criteria: CampaignFilterCriteria = {},
  options: { debug?: boolean; label?: string } = {},
): T[] => {
  const { debug = false, label = '[PostFilter]' } = options || {};
  const list = Array.isArray(posts) ? posts : [];
  const keywords = Array.isArray(criteria.keywords) ? criteria.keywords : [];
  const markets = Array.isArray(criteria.markets) ? criteria.markets : [];
  const platforms = Array.isArray(criteria.platforms) ? criteria.platforms : [];
  const accounts = Array.isArray(criteria.accounts) ? criteria.accounts : [];

  const baseCount = list.length;
  const kwLower = keywords.map((k) => String(k || '').toLowerCase()).filter(Boolean);

  // 모든 조건이 비어있으면 전체 데이터 반환
  const hasAnyCriteria = kwLower.length > 0
    || markets.length > 0
    || platforms.length > 0
    || accounts.length > 0;

  if (!hasAnyCriteria) {
    if (debug) {
      console.log(`${label} 모든 조건이 비어있어 전체 데이터를 반환합니다.`);
    }
    return list;
  }

  // 기본 리스트에 대한 키워드별 히트 수
  const keywordHitCounts: Record<string, number> = {};
  if (kwLower.length > 0) {
    for (const k of kwLower) {
      let hits = 0;
      for (const p of list) {
        if (postMatchesAnyKeyword(asFilterable(p), [k])) hits += 1;
      }
      keywordHitCounts[k] = hits;
    }
  }

  // 1단계: 키워드 OR 필터 (하나라도 키워드가 포함되면 됨)
  const stage1 = list.filter((p) => postMatchesAnyKeyword(asFilterable(p), kwLower));
  const afterKeywords = stage1.length;

  // 1단계 결과에 대한 마켓별 카운트
  const marketsLower = markets.map((m) => String(m || '').toLowerCase()).filter(Boolean);
  const marketHitCounts: Record<string, number> = {};
  if (marketsLower.length > 0) {
    for (const m of marketsLower) {
      marketHitCounts[m] = stage1.reduce(
        (acc, p) => acc + (String(asFilterable(p)?.market || '').toLowerCase() === m ? 1 : 0),
        0,
      );
    }
  }
  // 2단계: 마켓 OR 필터
  const stage2 = marketsLower.length === 0
    ? stage1
    : stage1.filter(
      (p) => marketsLower.includes(String(asFilterable(p)?.market || '').toLowerCase()),
    );
  const afterMarkets = stage2.length;

  // 2단계 결과에 대한 플랫폼별 카운트
  const platformsLower = platforms.map((m) => String(m || '').toLowerCase()).filter(Boolean);
  const platformHitCounts: Record<string, number> = {};
  if (platformsLower.length > 0) {
    for (const s of platformsLower) {
      platformHitCounts[s] = stage2.reduce((acc, p) => {
        const post = asFilterable(p);
        const src = String(post?.source || post?.platform || '').toLowerCase();
        return acc + (src === s ? 1 : 0);
      }, 0);
    }
  }
  // 3단계: 플랫폼 OR 필터
  const stage3 = platformsLower.length === 0
    ? stage2
    : stage2.filter((p) => {
      const post = asFilterable(p);
      return platformsLower.includes(String(post?.source || post?.platform || '').toLowerCase());
    });
  const afterPlatforms = stage3.length;

  // 3단계 결과에 대한 계정별 카운트
  // 채널(platform) + 계정(account) 둘 다 일치하는 것만 필터
  const accountKeysSet = new Set<string>();
  accounts.forEach((a) => {
    let plat = '';
    let acc = '';
    if (typeof a === 'object' && a !== null) {
      // 계정 항목 계약 — 객체면 이 네 필드 중에서 읽는다
      const entry = a as { platform?: unknown; platformId?: unknown; account?: unknown; username?: unknown };
      plat = String(entry?.platform || entry?.platformId || '').trim().toLowerCase();
      acc = String(entry?.account || entry?.username || '').trim().toLowerCase();
    } else {
      const str = String(a || '').trim();
      const parts = str.split('*');
      if (parts.length >= 2) {
        plat = parts[0].trim().toLowerCase();
        acc = parts[1].trim().toLowerCase();
      } else {
        acc = str.toLowerCase();
      }
    }
    if (plat && acc) {
      accountKeysSet.add(`${plat}*${acc}`);
    } else if (acc) {
      accountKeysSet.add(`*${acc}`);
    }
  });

  const accountHitCounts: Record<string, number> = {};
  if (accountKeysSet.size > 0) {
    Array.from(accountKeysSet).forEach((key) => {
      const [platPart, accPart] = key.startsWith('*') ? ['', key.slice(1)] : key.split('*');
      accountHitCounts[key] = stage3.reduce((acc, p) => {
        const post = asFilterable(p);
        const pPlat = String(post?.source || post?.platform || '').toLowerCase();
        const pAcc = String(post?.account || post?.username || '').toLowerCase();
        const platMatch = !platPart || pPlat === platPart;
        const accMatch = pAcc === accPart;
        return acc + (platMatch && accMatch ? 1 : 0);
      }, 0);
    });
  }

  // 4단계: 계정 OR 필터 (채널 + 계정명 둘 다 일치)
  const finalList = accountKeysSet.size === 0
    ? stage3
    : stage3.filter((p) => {
      const post = asFilterable(p);
      const pPlat = String(post?.source || post?.platform || '').toLowerCase();
      const pAcc = String(post?.account || post?.username || '').toLowerCase();
      if (!pAcc) return false;
      return Array.from(accountKeysSet).some((key) => {
        if (key.startsWith('*')) {
          return pAcc === key.slice(1);
        }
        const [platPart, accPart] = key.split('*');
        return pPlat === platPart && pAcc === accPart;
      });
    });

  if (debug) {
    console.log(`${label} 클라이언트 필터 요약`, {
      baseCount,
      keywords: kwLower,
      keywordHitCounts,
      afterKeywords,
      markets: marketsLower,
      marketHitCounts,
      afterMarkets,
      platforms: platformsLower,
      platformHitCounts,
      afterPlatforms,
      accountKeys: Array.from(accountKeysSet),
      accountHitCounts,
      afterAccounts: finalList.length,
    });
  }
  return finalList;
};

/**
 * 포스트가 account/title/content/author 중 어느 것에라도 제공된 키워드 중 하나를 포함하는지 확인합니다 (대소문자 무시).
 */
const hasAnyExplicitKeyword = (
  post: FilterablePost,
  keywords: unknown[] = [],
): boolean => {
  const kw = (Array.isArray(keywords) ? keywords : [])
    .map((k) => String(k || '').toLowerCase())
    .filter(Boolean);
  if (kw.length === 0) return true;
  const account = String(post?.account || post?.username || '').toLowerCase();
  const title = String(post?.title || '').toLowerCase();
  const content = String(post?.content || '').toLowerCase();
  const author = String(post?.author || '').toLowerCase();
  return kw.some((k) => account.includes(k) || title.includes(k) || content.includes(k) || author.includes(k));
};

/**
 * 명시적인 키워드 존재 여부로 리스트를 필터링합니다.
 */
export const filterByExplicitKeywords = <T,>(
  posts: T[] | null | undefined,
  keywords: unknown[] = [],
): T[] => {
  const list = Array.isArray(posts) ? posts : [];
  return list.filter((p) => hasAnyExplicitKeyword(asFilterable(p), keywords));
};

/**
 * 결과 내 재검색: 본문·계정 필드 중 선택된 곳에 부분 문자열이 포함되는 포스트만 남깁니다.
 * - `body`: title, content
 * - `account`: account, author
 * (레거시 저장값의 `brand` 키는 무시 — 사이드바에서 brands 배열 검색 미사용)
 */
export function filterPostsByRefineText<T>(
  posts: T[] | null | undefined,
  /** 없으면 필터하지 않는다 */
  refineText?: string | null,
  /** 없으면 기본 필드(`body`) */
  refineFieldKeys?: readonly string[] | null,
): T[] {
  const q = String(refineText ?? '').trim().toLowerCase();
  const list = Array.isArray(posts) ? posts : [];
  if (!q) return list;

  const raw = Array.isArray(refineFieldKeys) && refineFieldKeys.length > 0
    ? refineFieldKeys
    : ['body'];
  const keys = raw.filter((k) => k === 'body' || k === 'account');
  const effectiveKeys = keys.length > 0 ? keys : ['body'];

  return list.filter((item) => {
    const post = asFilterable(item);
    const hay = [];
    if (effectiveKeys.includes('body')) {
      hay.push(post?.title, post?.content);
    }
    if (effectiveKeys.includes('account')) {
      hay.push(post?.account, post?.author);
    }
    return hay.some((v) => String(v ?? '').toLowerCase().includes(q));
  });
}

type BrandClusterLike = {
  mainBrand?: unknown;
  similarBrands?: unknown[] | null;
};

/**
 * `SearchClientFilter`의 필터 옵션.
 *
 * ⚠️ 값 타입이 느슨한 것(`unknown`)은 계약이다 — 화면들이 저장된 필터 객체에서
 * 꺼낸 값을 검사 없이 넘기고, 구현이 배열/단일값·불리언을 런타임에 판별한다.
 */
export type SearchClientFilterOptions = {
  /** 미사용 — 서버 검색이 처리한다 (아래 주석 처리된 블록의 흔적) */
  searchKeyword?: unknown;
  /** 광고 평가 필터 (true: 광고/협찬, false: 일반) — boolean 또는 boolean[] */
  adsEval?: unknown;
  /** 브랜드 공식 계정만 보기 — boolean 또는 boolean[] */
  isOfficialAccount?: unknown;
  /** 레거시 별칭 (`isOfficialAccount`와 동일 역할) */
  isOfficial?: unknown;
  /** 긍/부정 필터 — 'positive' | 'negative' 외에는 미적용 */
  sentiment?: unknown;
  /** 토픽 정보 배열 [{ keywords: [] }] */
  selectedTopicsInfo?: { keywords?: unknown[] | null }[] | null;
  /** 선택된 브랜드 배열 */
  selectedBrands?: unknown[] | null;
  /** 브랜드 클러스터 데이터 (유사 브랜드 확장) */
  brandClusters?: BrandClusterLike[] | null;
  /** 캠페인 고정 필터 (keywordFilter) — mandatory OR·exclude AND·플랫폼·마켓 제한 */
  campaignFilter?: {
    mandatoryList?: unknown;
    excludeList?: unknown;
    platforms?: unknown;
    markets?: unknown;
  } | null;
  /** 선택된 키워드 칩 (OR 조건) */
  selectedKeywordChips?: unknown[];
  /** 선택된 계정 키 (platform*account) */
  selectedAccountKeys?: unknown[];
  /** 사용자 선택 플랫폼 (AND 조건) */
  selectedPlatformIds?: unknown[];
  /** 사용자 선택 국가 (AND 조건) */
  selectedCountryCodes?: unknown[];
  /** 사용자 선택 언어 (AND 조건) */
  selectedLanguageCodes?: unknown[];
  /** 상위 채널 선택 (세부 채널 판정에 쓴다) */
  selectedMainPlatformIds?: unknown[];
  /** 브랜드 채널 전체 선택 상태 */
  isAllBrandChannels?: boolean;
  brandXChannelActive?: boolean;
  brandInstagramChannelActive?: boolean;
};

/**
 * 범용 클라이언트 필터 함수 - 다양한 필터 조건을 하나의 함수로 처리
 *
 * 옵션 필드는 **전부 옵셔널이다** — 구현이 기본값을 갖거나 미지정을 "필터 없음"으로
 * 다룬다(아래 구조분해). 검색 화면은 15개 중 5개만 넘긴다.
 */
export const SearchClientFilter = <T,>(
  posts: T[] | null | undefined,
  filterOptions: SearchClientFilterOptions = {},
  options: {
    /** 디버그 로그 출력 */
    debug?: boolean;
    /** 디버그 라벨 */
    label?: string;
    /** true면 국가/마켓 정규화 값만 완전 일치로 비교 (base 분리 없음) */
    countryExactMatch?: boolean;
  } = {},
): T[] => {
  try {
    const { debug = false, label = '[UniversalFilter]', countryExactMatch = false } = options || {};
    const list = Array.isArray(posts) ? posts : [];

    if (list.length === 0) {
      if (debug) console.log(`${label} 빈 배열 입력 - 즉시 반환`);
      return [];
    }

    const {
      // searchKeyword,
      adsEval,
      isOfficial: isOfficialLegacy,
      isOfficialAccount: isOfficialAccountOption,
      sentiment,
      selectedTopicsInfo,
      selectedBrands,
      brandClusters = [],
      campaignFilter,
      selectedKeywordChips = [],
      selectedAccountKeys = [],
      selectedPlatformIds = [],
      selectedMainPlatformIds = [],
      selectedCountryCodes = [],
      selectedLanguageCodes = [],
      isAllBrandChannels = false,
      brandXChannelActive = true,
      brandInstagramChannelActive = true,
    } = filterOptions;

    // ✅ fast-path: 추가 클라이언트 필터가 없으면 원본 목록 그대로 반환
    // - selectedPlatformIds/selectedCountryCodes는 기본값([])이므로 Set/normalize 비용을 피합니다.
    const hasClientFiltersExceptPlatformCountry = !!campaignFilter
      || (Array.isArray(selectedKeywordChips) && selectedKeywordChips.length > 0)
      || (Array.isArray(selectedAccountKeys) && selectedAccountKeys.length > 0)
      || (sentiment === 'positive' || sentiment === 'negative')
      || (Array.isArray(adsEval) ? adsEval.length > 0 : (adsEval === true || adsEval === false))
      || (Array.isArray(isOfficialAccountOption) ? isOfficialAccountOption.length > 0 : !!isOfficialAccountOption)
      || (Array.isArray(isOfficialLegacy) ? isOfficialLegacy.length > 0 : !!isOfficialLegacy)
      || (Array.isArray(selectedTopicsInfo) && selectedTopicsInfo.length > 0)
      || (Array.isArray(selectedBrands) && selectedBrands.length > 0);

    const hasBrandChannelExclusion = isAllBrandChannels
      && (!brandXChannelActive || !brandInstagramChannelActive);

    const hasPlatformCountryFilter = (Array.isArray(selectedPlatformIds) && selectedPlatformIds.length > 0)
      || (Array.isArray(selectedCountryCodes) && selectedCountryCodes.length > 0)
      || (Array.isArray(selectedLanguageCodes) && selectedLanguageCodes.length > 0)
      || hasBrandChannelExclusion;

    if (!hasClientFiltersExceptPlatformCountry && !hasPlatformCountryFilter) {
      if (debug) console.log(`${label} fast-path(기본): ${list.length} (필터 없음)`);
      return list;
    }

    const filterIsOfficialAccountSel = Object.prototype.hasOwnProperty.call(filterOptions, 'isOfficialAccount')
      ? isOfficialAccountOption
      : isOfficialLegacy;

    let filtered = [...list];
    const baseCount = list.length;

    if (debug) {
      console.log(`${label} 필터링 시작`, {
        baseCount,
        // searchKeyword,
        adsEval,
        filterIsOfficialAccountSel,
        sentiment,
        topicsCount: selectedTopicsInfo?.length || 0,
        brandsCount: selectedBrands?.length || 0,
        brandClustersCount: (brandClusters ?? []).length,
        hasCampaignFilter: !!campaignFilter,
        keywordChipsCount: selectedKeywordChips.length,
        accountKeysCount: selectedAccountKeys.length,
      });
    }

    // 0. 캠페인 고정 필터 (최우선 적용)
    if (campaignFilter) {
      const beforeCount = filtered.length;
      const toTerms = (arr: unknown) => (Array.isArray(arr) ? arr : [])
        .map((s) => String(s || '').toLowerCase().trim())
        .filter(Boolean);
      const mandatoryTerms = toTerms(campaignFilter.mandatoryList);
      const excludeTerms = toTerms(campaignFilter.excludeList);
      const platformSet = new Set(
        (Array.isArray(campaignFilter.platforms) ? campaignFilter.platforms : []).map((v) => String(v).toLowerCase()),
      );
      const marketSet = new Set(
        (Array.isArray(campaignFilter.markets) ? campaignFilter.markets : []).map((v) => String(v).toLowerCase()),
      );

      const norm = (v: unknown) => String(v || '').toLowerCase();
      const collectFields = (post: FilterablePost) => [post?.title, post?.content, post?.author, post?.account].map((v) => norm(v));

      // 제외 키워드: 하나라도 포함되면 제외 (OR 조건)
      if (excludeTerms.length > 0) {
        filtered = filtered.filter((p) => {
          const fields = collectFields(asFilterable(p));
          const hasAnyExcluded = excludeTerms.some((term) => fields.some((f) => f.includes(term)));
          return !hasAnyExcluded;
        });
      }

      // 필수 키워드: 하나라도 포함되면 통과 (OR 조건)
      if (mandatoryTerms.length > 0) {
        filtered = filtered.filter((p) => {
          const fields = collectFields(asFilterable(p));
          return mandatoryTerms.some((term) => fields.some((f) => f.includes(term)));
        });
      }

      // 캠페인 플랫폼 제한
      if (platformSet.size > 0) {
        filtered = filtered.filter((p) => {
          const post = asFilterable(p);
          return platformSet.has(norm(post?.source || post?.platform));
        });
      }

      // 캠페인 마켓 제한
      if (marketSet.size > 0) {
        filtered = filtered.filter((p) => {
          const raw = norm(asFilterable(p)?.market);
          if (!raw) return false;
          const base = raw.includes('-') || raw.includes('_') ? raw.split('-')[0].split('_')[0] : raw;
          return marketSet.has(base) || marketSet.has(raw);
        });
      }

      if (debug) {
        console.log(`${label} 캠페인 고정 필터: ${beforeCount} → ${filtered.length}`, {
          mandatoryList: mandatoryTerms,
          excludeList: excludeTerms,
          platforms: Array.from(platformSet),
          markets: Array.from(marketSet),
        });
      }
    }

    // 0.5. 키워드칩/계정칩 OR 필터
    if (selectedKeywordChips.length > 0 || selectedAccountKeys.length > 0) {
      const beforeCount = filtered.length;
      const keywordSet = new Set(selectedKeywordChips.map((v) => String(v).toLowerCase()));
      const accountSet = new Set(selectedAccountKeys.map((v) => String(v).toLowerCase()));

      const containsSelectedKeyword = (post: FilterablePost) => {
        if (keywordSet.size === 0) return false;
        const title = String(post?.title || '').toLowerCase();
        const content = String(post?.content || '').toLowerCase();
        const account = String(post?.account || '').toLowerCase();
        const author = String(post?.author || '').toLowerCase();
        for (const kw of keywordSet) {
          if (!kw) continue;
          if (title.includes(kw) || content.includes(kw) || account.includes(kw) || author.includes(kw)) return true;
        }
        return false;
      };

      const matchesSelectedAccount = (post: FilterablePost) => {
        if (accountSet.size === 0) return false;
        const plt = String(post?.source || post?.platform || '').toLowerCase();
        const acc = String(post?.account || '').toLowerCase();
        if (!plt || !acc) return false;
        return accountSet.has(`${plt}*${acc}`);
      };

      filtered = filtered.filter((p) => {
        const post = asFilterable(p);
        return containsSelectedKeyword(post) || matchesSelectedAccount(post);
      });

      if (debug) {
        console.log(`${label} 키워드/계정 칩 OR 필터: ${beforeCount} → ${filtered.length}`, {
          keywordChips: Array.from(keywordSet),
          accountKeys: Array.from(accountSet),
        });
      }
    }

    // 2. 페르소나 필터링 — 다중 선택은 OR(합집합), 전체 선택 시 필터 미적용
    const personaAdsEval = Array.isArray(adsEval)
      ? adsEval.filter((v): v is boolean => v === true || v === false)
      : (adsEval === true || adsEval === false ? [adsEval] : []);
    const wantsOfficialPersona = filterIsOfficialAccountSel === true
      || (Array.isArray(filterIsOfficialAccountSel) && filterIsOfficialAccountSel.includes(true));
    const hasPersonaFilter = personaAdsEval.length > 0 || wantsOfficialPersona;

    if (hasPersonaFilter) {
      const beforeCount = filtered.length;
      filtered = filtered.filter((item) => isPostPersonaSelected(item, {
        personaAdsEval,
        personaIsOfficial: wantsOfficialPersona,
      }));

      if (debug) {
        console.log(`${label} 페르소나 필터링(OR: adsEval=${JSON.stringify(personaAdsEval)}, official=${wantsOfficialPersona}): ${beforeCount} → ${filtered.length}`);
      }
    }

    // 4. 긍부정 필터링 (evidence 배열 존재 여부 기준)
    if (sentiment === 'positive' || sentiment === 'negative') {
      const beforeCount = filtered.length;
      if (sentiment === 'positive') {
        filtered = filtered.filter((item) => {
          const evidence = asFilterable(item)?.sentimentEvidencePositive;
          return Array.isArray(evidence) ? evidence.length > 0 : !!evidence;
        });
      } else if (sentiment === 'negative') {
        filtered = filtered.filter((item) => {
          const evidence = asFilterable(item)?.sentimentEvidenceNegative;
          return Array.isArray(evidence) ? evidence.length > 0 : !!evidence;
        });
      }
      if (debug) {
        console.log(`${label} sentiment evidence 필터링(${sentiment}): ${beforeCount} → ${filtered.length}`);
      }
    }

    // 5. 토픽 필터링
    if (selectedTopicsInfo && selectedTopicsInfo.length > 0) {
      const beforeCount = filtered.length;
      const allKeywords = selectedTopicsInfo
        .flatMap((topic) => topic?.keywords ?? [])
        .filter(Boolean)
        .map((keyword) => String(keyword));

      if (allKeywords.length > 0) {
        filtered = filtered.filter((item) => {
          const post = asFilterable(item);
          const fields = [post?.title, post?.content, post?.author, post?.account].map((f) => (f || '').toString());
          return allKeywords.some((keyword) => fields.some((field) => field.includes(keyword)));
        });
      }

      if (debug) {
        console.log(`${label} 토픽 필터링: ${beforeCount} → ${filtered.length} (키워드 수: ${allKeywords.length})`);
      }
    }

    // 5. 브랜드 필터링 (유사 브랜드 확장 + recognized !== false 조건)
    if (selectedBrands && selectedBrands.length > 0) {
      const beforeCount = filtered.length;
      const expandedBrandsSet = getExpandedBrandSet(selectedBrands, brandClusters ?? []);

      filtered = filtered.filter((item) => {
        const rawBrands = asFilterable(item)?.brands;
        const itemBrands = Array.isArray(rawBrands) ? rawBrands : [];
        if (itemBrands.length === 0) return false;
        const normalizedItemBrands = itemBrands.map((b) => String(b || '').toLowerCase().trim());

        // 확장된 브랜드 세트로 매칭
        return normalizedItemBrands.some((b) => expandedBrandsSet.has(b));
      });

      if (debug) {
        console.log(`${label} 브랜드 필터링: ${beforeCount} → ${filtered.length} (선택된 브랜드: ${selectedBrands.length}, 확장된 브랜드: ${expandedBrandsSet.size})`);
      }
    }

    // 6. 사용자 선택 플랫폼/국가/언어 필터 (AND 조건)
    const platformSet = new Set(selectedPlatformIds.map((v) => String(v).toLowerCase()));
    const countrySet = new Set(selectedCountryCodes.map((v) => String(v).toLowerCase()));
    const languageSet = new Set(
      (Array.isArray(selectedLanguageCodes) ? selectedLanguageCodes : []).map((v) => String(v).toLowerCase()),
    );
    const norm = (v: unknown) => String(v || '').toLowerCase();

    const mainPlatformIds = Array.isArray(selectedMainPlatformIds)
      ? selectedMainPlatformIds.map((v) => String(v).toLowerCase()).filter(Boolean)
      : [];

    const matchesPlatform = (post: FilterablePost) => matchesPlatformSubChips(
      post,
      platformSet,
      norm,
      {
        isAllBrandChannels,
        selectedMainPlatformIds: mainPlatformIds,
        brandXChannelActive,
        brandInstagramChannelActive,
      },
    );

    const matchesCountry = (post: FilterablePost) => {
      if (countrySet.size === 0) return true;
      const raw = norm(post?.market || post?.country);
      if (!raw) return false;
      if (countryExactMatch) {
        return countrySet.has(raw);
      }
      const base = raw.includes('-') || raw.includes('_') ? raw.split('-')[0].split('_')[0] : raw;
      return countrySet.has(base) || countrySet.has(raw);
    };

    const matchesLanguage = (post: FilterablePost) => {
      if (languageSet.size === 0) return true;
      const raw = norm(post?.tL || post?.language);
      if (!raw) return false;
      return languageSet.has(raw);
    };

    filtered = filtered.filter((p) => {
      const post = asFilterable(p);
      return matchesPlatform(post) && matchesCountry(post) && matchesLanguage(post);
    });

    if (debug) {
      console.log(`${label} 사용자 플랫폼/국가/언어 필터: ${baseCount} → ${filtered.length}`, {
        platforms: Array.from(platformSet),
        countries: Array.from(countrySet),
        languages: Array.from(languageSet),
      });
    }

    if (debug) {
      console.log(`${label} 필터링 완료: ${baseCount} → ${filtered.length}`);
    }

    return filtered;
  } catch (error) {
    // 로그 페이로드용 — catch 변수는 unknown이라 읽는 필드만 좁힌다
    const err = error as { message?: unknown; stack?: unknown };
    log.error(SERVICE_NAME, 'SearchClientFilter - 필터링 실패', {
      err: err?.message || String(error),
      stack: err?.stack,
      postsCount: Array.isArray(posts) ? posts.length : 0,
      filterOptions,
      side: 'client',
      toastMessage: '필터링 실패했습니다. 재시도 해주세요.',
    });
    return [];
  }
};

/**
 * 브랜드 클러스터에서 확장된 브랜드 세트를 생성하는 헬퍼 함수.
 * 세트 원소는 클러스터가 준 원본 값 그대로다 (정규화는 호출부의 몫).
 */
const getExpandedBrandSet = (
  selectedBrands: unknown[] | null | undefined,
  brandClusters: BrandClusterLike[] | null | undefined,
): Set<unknown> => {
  if (!selectedBrands || selectedBrands.length === 0 || !brandClusters || brandClusters.length === 0) {
    return new Set(selectedBrands);
  }

  const expanded = new Set<unknown>();
  brandClusters.forEach((cluster) => {
    if (
      selectedBrands.includes(cluster.mainBrand)
      || (cluster.similarBrands && cluster.similarBrands.some((b) => selectedBrands.includes(b)))
    ) {
      expanded.add(cluster.mainBrand);
      (cluster.similarBrands || []).forEach((b) => expanded.add(b));
    }
  });

  return expanded;
};
